import { useState } from 'react';
import { Pagination } from '@mui/material';
import * as TopList from 'components/Layout/Tiles/ListContainer';
import { TileContent } from 'components/Layout/PageContainer';
import { Attribute, useModelService } from 'services/model/model.service';
import { DataEntry } from 'models/data';
import { EmptyState } from 'components/EmptyState';

interface SalesPerformanceListProps {
  isComparisonActive: boolean;
  entries: DataEntry[];
  attribute: Attribute;
  showFlag?: boolean;
  getIcon?: (entry: DataEntry) => React.ReactNode;
  onClick?: (id: string) => void;
}

export function SalesPerformanceList({
  entries,
  isComparisonActive,
  attribute,
  showFlag = false,
  getIcon = undefined,
  onClick = undefined,
}: SalesPerformanceListProps) {
  const { getMeasuresByMetrics } = useModelService();

  const [pageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  const [slsQty, slsPrdNum] = getMeasuresByMetrics(['slsQty', 'slsPrdNum'], 'OD');
  if (entries.length === 0) {
    return <EmptyState />;
  }
  return (
    <TileContent className="flex flex-col justify-between items-center">
      <TopList.ListContainer>
        {entries
          .sort((a, b) => {
            const aQty = slsQty.getValue(a.values);
            const aPrd = slsPrdNum.getValue(a.values);
            const bQty = slsQty.getValue(b.values);
            const bPrd = slsPrdNum.getValue(b.values);

            const aAvg = aPrd === 0 ? 0 : aQty / aPrd;
            const bAvg = bPrd === 0 ? 0 : bQty / bPrd;

            return bAvg - aAvg;
          })
          .slice((currentPage - 1) * pageSize, currentPage * pageSize)
          .map(entry => (
            <TopList.ListRow
              key={attribute.getValue(entry.attributes)}
              onClick={onClick ? () => onClick(attribute.getRawValue(entry.attributes)) : undefined}
            >
              <TopList.NameCell
                hideColorBar
                name={attribute.getValue(entry.attributes)}
                countryCode={showFlag ? attribute.getRawValue(entry.attributes) : undefined}
                icon={getIcon?.(entry)}
              />

              {/* Avg. Sold Items per Product */}
              <TopList.MetricCell
                label="model.calculated.metric.avgSoldItemsPerProduct.name"
                value={
                  slsPrdNum.getValue(entry.values) === 0
                    ? 0
                    : slsQty.getValue(entry.values) / slsPrdNum.getValue(entry.values)
                }
                comparisonValue={
                  slsPrdNum.getValue(entry.compareValues) === 0
                    ? 0
                    : slsQty.getValue(entry.compareValues) / slsPrdNum.getValue(entry.compareValues)
                }
                unit={slsPrdNum.metric.unit}
                comparisonActive={isComparisonActive}
                size="lg"
              />

              {/* Sold Products */}
              <TopList.MetricCell
                measure={slsPrdNum}
                value={slsPrdNum.getValue(entry.values)}
                comparisonValue={slsPrdNum.getValue(entry.compareValues)}
                unit={slsPrdNum.metric.unit}
                comparisonActive={isComparisonActive}
                size="sm"
              />

              {/* Sold Items */}
              <TopList.MetricCell
                measure={slsQty}
                value={slsQty.getValue(entry.values)}
                comparisonValue={slsQty.getValue(entry.compareValues)}
                unit={slsQty.metric.unit}
                comparisonActive={isComparisonActive}
                size="sm"
              />
            </TopList.ListRow>
          ))}
      </TopList.ListContainer>
      {Math.ceil(entries.length / pageSize) > 1 && (
        <Pagination
          className="mt-4"
          size="small"
          count={Math.ceil(entries.length / pageSize)}
          page={currentPage}
          onChange={(_event, page) => setCurrentPage(page)}
        />
      )}
    </TileContent>
  );
}
